import React, { KeyboardEvent, useRef, useState } from 'react';
import {
  Field,
  Image,
  ImageField,
  LinkField,
  TextField,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { FileField } from '@sitecore-jss/sitecore-jss-react/types/components/File';
import classNames from 'classnames';
import { EmblaOptionsType } from 'embla-carousel';
import { sanitize } from 'isomorphic-dompurify';

import { Button } from '@gn-audio/jss-components/components';
import { getContrastThemeClass, resolveColorProperty } from '@gn-audio/jss-components/utils';

import { JabraSlider, JabraSliderRef } from 'common/components/JabraSlider/JabraSlider';
import { PillsSliderNavigation } from 'common/components/JabraSlider/PillsSliderNavigation';
import { mapSharedPropertiesToFields } from 'common/getters';
import { SitecoreFieldTypes } from 'common/types/sitecore';
import { usePlaceholderContainer } from 'components/Product/KSP/Placeholder/PlaceholderContainerContext';
import VideoEmbed from 'composables/Product/VideoEmbed/VideoEmbed';
import { ComponentProps } from 'lib/component-props';
interface ContentSliderSpotProps extends ComponentProps {
  fields: SitecoreFieldTypes;
  props: {
    [name: string]: string;
  };
}

type ContentSliderSpotFields = {
  background: Field<string>;
  description: Field<string>;
  headline: Field<string>;
  slides: ContentSliderItem[];
};

type ContentSliderItem = {
  description: TextField;
  headline: TextField;
  id: TextField;
  image: ImageField;
  link: LinkField;
  linkTitle: TextField;
  size: Field<'full' | 'half' | 'quarter'>;
  video: FileField;
  youkuVideoId: TextField;
  youtubeVideoId: TextField;
};

const sizeToClass = {
  full: 'flex-[0_0_100%]',
  half: 'flex-[0_0_100%] md:flex-[0_0_50%]',
  quarter: 'flex-[0_0_100%] md:flex-[0_0_25%]',
};

const shouldEnableSlider = (slides: ContentSliderItem[]) => {
  const { fullCount, halfCount, quarterCount } = slides.reduce(
    (acc, slide) => {
      if (slide.size.value === 'full') acc.fullCount++;
      if (slide.size.value === 'half') acc.halfCount++;
      if (slide.size.value === 'quarter') acc.quarterCount++;
      return acc;
    },
    { fullCount: 0, halfCount: 0, quarterCount: 0 }
  );

  return (
    fullCount >= 1 || halfCount >= 2 || quarterCount >= 4 || (quarterCount >= 2 && halfCount >= 1)
  );
};

const ContentSliderSpot: React.FC<ContentSliderSpotProps> = ({ fields: unparsedFields }) => {
  const fields = mapSharedPropertiesToFields<ContentSliderSpotFields>(unparsedFields);
  const { background, description, headline, slides } = fields;
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [startIndex] = useState<number>(0);
  const jabraSlider = useRef<JabraSliderRef>(null);
  const { isInsidePlaceholder } = usePlaceholderContainer();

  const backgroundColorHex = resolveColorProperty(background?.value?.toLowerCase());

  const containerClass = classNames({
    'bg-transparent': isInsidePlaceholder && !backgroundColorHex,
  });

  const getLightOrDarkTheme = backgroundColorHex ? getContrastThemeClass(backgroundColorHex) : null;

  if (!slides || slides.length === 0) {
    return <div className="container-fluid">No slides found</div>;
  }
  const enableSlider = shouldEnableSlider(slides);

  const sliderSettings: EmblaOptionsType = {
    containScroll: false,
    active: enableSlider,
    align: 'start',
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (jabraSlider.current) {
      if (event.key === 'ArrowLeft') {
        jabraSlider.current.prev();
      } else if (event.key === 'ArrowRight') {
        jabraSlider.current.next();
      }
    }
  };

  const handleMoveToSlide = (index: number) => {
    jabraSlider.current?.goTo(index);
  };

  return (
    <div
      style={backgroundColorHex && { backgroundColor: backgroundColorHex }}
      onKeyDown={handleKeyDown}
      className={classNames('container-fluid', containerClass, getLightOrDarkTheme)}
    >
      <div className="m-auto mb-12 flex max-w-[1440px] flex-col justify-between gap-8 px-3 md:flex-row md:items-center 2xl:px-0">
        <div>
          <div className="heading-2 mb-0">{headline.value}</div>
          {description && <p className="paragraph mt-4">{description.value}</p>}
        </div>
        {enableSlider && (
          <div className="self-end">
            <PillsSliderNavigation
              activeIndex={activeIndex}
              total={fields.slides.length}
              buttonVariant="primary"
              sliderButtonPosition="start"
              goTo={handleMoveToSlide}
            />
          </div>
        )}
      </div>
      <JabraSlider
        mainClassName="relative w-full focus:outline-none max-w-[1440px]"
        className="overflow-hidden"
        startIndex={startIndex}
        overflow
        sliderSettings={sliderSettings}
        ref={jabraSlider}
        onChange={setActiveIndex}
        showSlideArrows={false}
      >
        {slides.map((unparsedSlideFields) => {
          const {
            id,
            youtubeVideoId,
            youkuVideoId,
            size,
            video,
            image,
            headline,
            description,
            link,
            linkTitle,
          } = mapSharedPropertiesToFields<ContentSliderItem>(unparsedSlideFields);
          const hasVideoSrc = youtubeVideoId?.value || youkuVideoId?.value;
          return (
            <div
              key={id.value}
              className="w-full"
              data-single-slide-classname={classNames('px-3', sizeToClass[size.value])}
            >
              <figure className="w-full">
                {hasVideoSrc ? (
                  <VideoEmbed
                    youtubeId={youtubeVideoId?.value as string}
                    youkuId={youkuVideoId?.value as string}
                    className="aspect-video w-full rounded-lg"
                  />
                ) : video?.value ? (
                  <video
                    src={video.value.src}
                    autoPlay
                    loop
                    muted
                    className="w-full max-w-full rounded-lg"
                  />
                ) : image?.value?.src ? (
                  <Image
                    className="h-full w-full max-w-full rounded-lg object-cover"
                    field={image.value}
                  />
                ) : null}
              </figure>
              <div className="mt-6 px-3">
                <p
                  className="heading-5 font-bold"
                  dangerouslySetInnerHTML={{ __html: sanitize(headline.value as string) }}
                />
                <p
                  className="paragraph"
                  dangerouslySetInnerHTML={{ __html: sanitize(description.value as string) }}
                />
                {link?.value?.href && (
                  <Button
                    variant="tertiary"
                    asLink
                    size="m"
                    href={link.value.href}
                    className="mt-4 flex"
                  >
                    {linkTitle?.value}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </JabraSlider>
    </div>
  );
};

export default withDatasourceCheck()(ContentSliderSpot);
